import React, { useState, useEffect, useRef } from 'react';
import InfoTooltip from './InfoTooltip';
import { useApplyPlatformSettingShortcodes } from 'src/utils/usePlatformSettingDisplayText';

export default function CustomSelect({ setting, values, handleChange, hasIdentifier, sourceChannelName }) {
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes(sourceChannelName);
  const optionLabel = (displayName?: string | null) => formatSettingDisplayText(displayName);
  const allOptions = setting.setting_options;
  const [searchTerm, setSearchTerm] = useState('');
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
      setIsUserTyping(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const limit = 10;

  const selectedOption = allOptions?.find(
    (opt) => opt.name === values[setting.name]
  );

  const filteredOptions = searchTerm
    ? allOptions.filter((opt) =>
        optionLabel(opt.display_name).toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allOptions;

  useEffect(() => {
    setVisibleOptions(filteredOptions?.slice(0, limit));
  }, [searchTerm]);

 const loadMore = () => {
  setVisibleOptions((prev) => {
    const next = filteredOptions?.slice(0, prev.length + limit);
    return next;
  });
  setLoadingMore(false);
};


 const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
  const isBottom = scrollHeight - scrollTop <= clientHeight + 1;

  if (isBottom && !loadingMore && visibleOptions?.length < filteredOptions?.length) {
    setLoadingMore(true);
    loadMore();
  }
};


  const handleSelect = (item: any) => {
    handleChange({
      target: { name: setting.name, value: item.name },
    });
    setSearchTerm('');
    setIsUserTyping(false);
    setDropdownOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsUserTyping(true);
    setDropdownOpen(true);
  };

  const handleInputFocus = () => {
    setDropdownOpen(true);
  };

  return (
    <div ref={containerRef} className="position-relative w-100" style={{paddingRight: '5.5rem'}}>
         {setting.display_name && (
      <label className="form-label required" htmlFor={setting.name}>
        {optionLabel(setting.display_name)}
        &nbsp;&nbsp;
        {setting.description && (
          <InfoTooltip message={setting.description} sourceChannelName={sourceChannelName} />
        )}
      </label>
    )}
      <input
        type="text"
        name={setting.name}
        ref={inputRef}
        className='form-select form-select-lg'
        placeholder={optionLabel(setting.placeholder)}
        value={
          isUserTyping
            ? searchTerm
            : optionLabel(selectedOption?.display_name)
        }
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        autoComplete="off"
        required
        disabled={hasIdentifier[setting?.name]}
      />

      {dropdownOpen && (
        <div
          className="dropdown-menu show w-100 mt-0 border shadow overflow-auto fs-5"
          style={{ maxHeight: '200px' }}
          onScroll={handleScroll}
        >
          {visibleOptions?.length === 0 ? (
            <div className="dropdown-item text-muted">No results</div>
          ) : (
            visibleOptions?.map((item: any) => (
              <button
                key={item.id}
                className="dropdown-item"
                type="button"
                onClick={() => handleSelect(item)}
              >
                {optionLabel(item.display_name)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
